import { downloadData, type ColumnDef } from './excelUtils';
import {
  WINE_LIST_SCORE_SOURCES,
  type WineListRender,
  type WineListRenderItem,
  type WineListScoreEntry,
} from '../types/venue';

/**
 * Designer-facing XLSX export for a wine list preview.
 *
 * Reuses the WineListRender DTO the preview already fetches — no extra round
 * trip. Sections come out in render order (server sorts by position). Within
 * each section, items are sorted by the chosen ExportSortMode.
 *
 * Columns: Section, Wine Name, Vintage, Format, Score, Price.
 */

export type ExportSortMode =
  | 'vintage-asc'  // Old → Young
  | 'vintage-desc' // Young → Old
  | 'price-asc'    // Price Low → High
  | 'price-desc';  // Price High → Low

export const EXPORT_SORT_LABELS: Record<ExportSortMode, string> = {
  'vintage-asc': 'Old → Young',
  'vintage-desc': 'Young → Old',
  'price-asc': 'Price Low → High',
  'price-desc': 'Price High → Low',
};

/**
 * Extract a 4-digit year from a vintage string for sorting.
 * Returns null for NV / MV / null / empty / non-numeric — those rows sort to
 * the end for both directions.
 */
function vintageSortKey(v: string | null | undefined): number | null {
  if (!v) return null;
  const match = v.match(/\d{4}/);
  if (!match) return null;
  return Number(match[0]);
}

/** Resolve the price to use for sorting + export: bottlePrice first, then legacy price. */
function priceValue(item: WineListRenderItem): number | null {
  if (item.bottlePrice != null) return item.bottlePrice;
  if (item.price != null) return item.price;
  return null;
}

/**
 * Sort items by the chosen mode. Items missing the relevant key (NV/MV/null
 * vintage, null price) always sort to the end regardless of direction, so
 * the designer never has blanks interspersed mid-list.
 */
function sortItems(
  items: WineListRenderItem[],
  mode: ExportSortMode,
): WineListRenderItem[] {
  const copy = items.slice();
  copy.sort((a, b) => {
    if (mode === 'vintage-asc' || mode === 'vintage-desc') {
      const ka = vintageSortKey(a.vintage);
      const kb = vintageSortKey(b.vintage);
      if (ka === null && kb === null) return 0;
      if (ka === null) return 1;
      if (kb === null) return -1;
      return mode === 'vintage-asc' ? ka - kb : kb - ka;
    }
    // price modes
    const pa = priceValue(a);
    const pb = priceValue(b);
    if (pa === null && pb === null) return 0;
    if (pa === null) return 1;
    if (pb === null) return -1;
    return mode === 'price-asc' ? pa - pb : pb - pa;
  });
  return copy;
}

/**
 * Build the Score cell: every available score joined in source order as
 * "JS 96 / WA 94". Empty string when no scores. Preserves the source order
 * from WINE_LIST_SCORE_SOURCES (JS, WA, WS, AV) so the column reads
 * consistently across rows.
 */
function formatScores(scores: WineListScoreEntry[] | undefined): string {
  if (!scores || scores.length === 0) return '';
  const bySource = new Map<string, number>();
  for (const e of scores) bySource.set(e.source, e.score);
  const parts: string[] = [];
  for (const src of WINE_LIST_SCORE_SOURCES) {
    const v = bySource.get(src);
    if (v != null) parts.push(`${src} ${v}`);
  }
  return parts.join(' / ');
}

/** Format bottle size as "750 ml" or "1.5 L"; empty when unknown. */
function formatSizeForExport(sizeMl: number | null): string {
  if (sizeMl == null) return '';
  return sizeMl >= 1000 ? `${sizeMl / 1000} L` : `${sizeMl} ml`;
}

/** Numeric price formatted to 2 decimals; empty string when null. */
function formatPriceForExport(price: number | null): string {
  return price == null ? '' : price.toFixed(2);
}

function buildRows(
  render: WineListRender,
  mode: ExportSortMode,
): Record<string, string>[] {
  const rows: Record<string, string>[] = [];
  for (const section of render.sections) {
    const sorted = sortItems(section.items, mode);
    for (const item of sorted) {
      rows.push({
        Section: section.name,
        'Wine Name': item.name ?? '',
        Vintage: item.vintage ?? '',
        Format: formatSizeForExport(item.bottleSize),
        Score: formatScores(item.scores),
        Price: formatPriceForExport(priceValue(item)),
      });
    }
  }
  return rows;
}

const EXPORT_COLUMNS: ColumnDef[] = [
  { header: 'Section', example: 'Reds' },
  { header: 'Wine Name', example: 'Barolo Castiglione' },
  { header: 'Vintage', example: '2019' },
  { header: 'Format', example: '750 ml' },
  { header: 'Score', example: 'JS 96 / WA 94' },
  { header: 'Price', example: '85.00' },
];

/**
 * Generate and download a designer-facing XLSX for the given rendered wine
 * list, with items inside each section sorted by `mode`.
 */
export function exportWineListXlsx(
  render: WineListRender,
  mode: ExportSortMode,
): void {
  const moduleName = `${render.list.name || 'wine-list'} designer`;
  downloadData(moduleName, EXPORT_COLUMNS, buildRows(render, mode));
}
